import React, { useEffect, useMemo, useRef } from 'react';
import { Box, GlobalStyles } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BarGraph from './BarGraph';
import ColumnWithRotated from './ColumnWithRotated';
import ColumnWithRotatedTime from './ColumnWithRotatedTime';
import CurvedColumns from './CurvedColumns';
import CurvedColumnsTime from './CurvedColumnsTime';
import LayeredColumn from './LayeredColumn';
import LayeredColumnTime from './LayeredColumnTime';
import LayeredColumnTimeDate from './LayeredColumnTimeDate';
import LineGraph from './LineGraph';
import PartitionedBar from './PartitionedBar';
import PieGraph from './PieGraph';
import PieGraphAnalitycs from './PieGraphAnalytics';
import PieGraphDashboard from './PieGraphDashboard';
import Puntuality from './Puntuality';
import PyramidGraph from './PyramidGraph';
import StackedColumn from './StackedColumn';
import StackedColumnAgent from './StackedColumnAgent';

/**
 * Props:
 *  - type: string
 *  - data: any
 *  - params: { id: string, ... }
 *  - ...any original props forwarded to concrete graph classes
 */
const GraphComponent = (props) => {
  const { type, data, params } = props;
  const theme = useTheme();
  const chartRef = useRef(null);
  const GraphCtor = useMemo(() => {
    switch (type) {
      case 'line': return LineGraph;
      case 'pie': return PieGraph;
      case 'pieDashboard': return PieGraphDashboard;
      case 'pieAnalytics': return PieGraphAnalitycs;
      case 'bar': return BarGraph;
      case 'partitionedBar': return PartitionedBar;
      case 'curvedColumns': return CurvedColumns;
      case 'curvedColumnsTime': return CurvedColumnsTime;
      case 'columnWithRotated': return ColumnWithRotated;
      case 'columnWithRotatedTime': return ColumnWithRotatedTime;
      case 'stackedColumn': return StackedColumn;
      case 'stackedColumnAgent': return StackedColumnAgent;
      case 'layeredColumn': return LayeredColumn;
      case 'layeredColumnTime': return LayeredColumnTime;
      case 'layeredColumnTimeDate': return LayeredColumnTimeDate;
      case 'puntualityGraph': return Puntuality;
      case 'pyramid': return PyramidGraph;
      default: return null;
    }
  }, [type]);
  useEffect(() => {
    if (!GraphCtor || !params?.id) return;
    const graph = new GraphCtor(data, props);
    graph.createGraph(params, graph.series);
    chartRef.current = graph.getGraph();
    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GraphCtor, data, params]);
  const exportBg = theme.palette.mode === 'dark'
    ? theme.palette.background.paper
    : theme.palette.common.white;
  const exportHover = theme.palette.mode === 'dark'
    ? theme.palette.action.hover
    : theme.palette.background.default;
  const shadow = theme.palette.mode === 'dark'
    ? '0 2px 12px rgba(0,0,0,0.6)'
    : '-3px 10px 43px -26px rgba(0,0,0,0.75)';
  const textColor = '#FFFFFF';
  return (
    <>
      <GlobalStyles styles={{
        '.amcharts-amexport-item': {
          backgroundColor: `${exportBg} !important`,
          opacity: '1 !important',
          boxShadow: shadow,
        },
        '.amcharts-amexport-item:hover': {
          backgroundColor: `${exportHover} !important`,
        },
        '.amcharts-amexport-top .amcharts-amexport-item > .amcharts-amexport-menu': {
          top: '-3px !important',
          left: '2px',
          backgroundColor: `${exportBg} !important`,
          opacity: '1 !important',
          boxShadow: shadow,
        },
        '.amcharts-main-div g[aria-label], .amcharts-main-div g[aria-label] text, .amcharts-main-div g[aria-label] tspan, .amcharts-main-div text, .amcharts-main-div tspan': {
        fill: `${textColor} !important`,
        color: `${textColor} !important`,
      },
      }} />
      <Box
        id={params ? params.id : undefined}
        sx={{
          width: '100%',
          height: '100%',
          bgcolor: 'transparent',
        }}
      />
    </>
  );
};
export default GraphComponent;
//Original code:
/*import React, { Component } from 'react';
import BarGraph from './BarGraph';
import ColumnWithRotated from './ColumnWithRotated';
import ColumnWithRotatedTime from './ColumnWithRotatedTime';
import CurvedColumns from './CurvedColumns';
import CurvedColumnsTime from './CurvedColumnsTime';
import LayeredColumn from './LayeredColumn';
import LayeredColumnTime from './LayeredColumnTime';
import LayeredColumnTimeDate from './LayeredColumnTimeDate';
import LineGraph from './LineGraph';
import PartitionedBar from './PartitionedBar';
import PieGraph from './PieGraph';
import PieGraphAnalitycs from './PieGraphAnalytics';
import PieGraphDashboard from './PieGraphDashboard';
import Puntuality from './Puntuality';
import PyramidGraph from './PyramidGraph';
import StackedColumn from './StackedColumn';
import StackedColumnAgent from './StackedColumnAgent';

class GraphComponent extends Component {
	constructor(props) {
		super(props);
		try {
			this.initalState = {
				type: props.type,
				data: props.data,
				params: props.params,
			};
			this.state = this.initalState;
		} catch (error) {
			console.error(error);
		}
	}
	componentDidUpdate(prevProps, prevState) {
		if (this.props.params !== this.state.params || this.props.data !== this.state.data) {
			const { state } = this;
			state.type = this.props.type;
			state.data = this.props.data;
			state.params = this.props.params;
			this.setState(state);
			this.prepareGraph();
		}
	}
	componentDidMount() {
		this.prepareGraph();
	}
	prepareGraph() {
		let graph;
		if (this.state.type === 'line') graph = new LineGraph(this.state.data, this.props);
		else if (this.state.type === 'pie') graph = new PieGraph(this.state.data, this.props);
		else if (this.state.type === 'pieDashboard') graph = new PieGraphDashboard(this.state.data, this.props);
		else if (this.state.type === 'pieAnalytics') graph = new PieGraphAnalitycs(this.state.data, this.props);
		else if (this.state.type === 'bar') graph = new BarGraph(this.state.data);
		else if (this.state.type === 'partitionedBar') graph = new PartitionedBar(this.state.data, this.props);
		else if (this.state.type === 'curvedColumns') graph = new CurvedColumns(this.state.data, this.props);
		else if (this.state.type === 'curvedColumnsTime') graph = new CurvedColumnsTime(this.state.data, this.props);
		else if (this.state.type === 'columnWithRotated') graph = new ColumnWithRotated(this.state.data, this.props);
		else if (this.state.type === 'columnWithRotatedTime')
			graph = new ColumnWithRotatedTime(this.state.data, this.props);
		else if (this.state.type === 'stackedColumn') graph = new StackedColumn(this.state.data, this.props);
		else if (this.state.type === 'stackedColumnAgent') graph = new StackedColumnAgent(this.state.data, this.props);
		else if (this.state.type === 'layeredColumn') graph = new LayeredColumn(this.state.data, this.props);
		else if (this.state.type === 'layeredColumnTime') graph = new LayeredColumnTime(this.state.data, this.props);
		else if (this.state.type === 'layeredColumnTimeDate')
			graph = new LayeredColumnTimeDate(this.state.data, this.props);
		else if (this.state.type === 'puntualityGraph') graph = new Puntuality(this.state.data, this.props);
		else if (this.state.type === 'pyramid') graph = new PyramidGraph(this.state.data, this.props);

		graph.createGraph(this.state.params, this.state.series);
		this.chart = graph.getGraph();
	}
	componentWillUnmount() {
		if (this.chart) {
			this.chart.dispose();
		}
	}
	render() {
		return <div id={this.props.params ? this.props.params.id : null} style={{ width: '100%', height: '100%' }} />;
	}
}
export default GraphComponent;
*/
